import { createClient } from '@/lib/supabase/client';

// ─── Normalize session_status enum to lowercase ───────────────────────────────
// DB enum values: draft | planned | confirmed | changed | cancelled
// NEVER send UI labels (Draft, Planned, Confirmed, Changed, Cancelled) to DB
export function normalizeStatus(status: string): string {
  return (status || 'draft').toLowerCase();
}

// Central helper — converts any UI label or unknown value to a valid lowercase enum string.
// For new template-created sessions, unknown/empty values default to 'planned'.
export function normalizeSessionStatus(value: string, defaultValue: 'draft' | 'planned' = 'draft'): string {
  const map: Record<string, string> = {
    draft:      'draft',
    planned:    'planned',
    confirmed:  'confirmed',
    changed:    'changed',
    cancelled:  'cancelled',
    // UI label variants (capitalized)
    Draft:      'draft',
    Planned:    'planned',
    Confirmed:  'confirmed',
    Changed:    'changed',
    Cancelled:  'cancelled',
  };
  const normalized = map[(value || '').trim()] ?? (value || '').toLowerCase().trim();
  const valid = ['draft', 'planned', 'confirmed', 'changed', 'cancelled'];
  return valid.includes(normalized) ? normalized : defaultValue;
}

// ─── Normalize a LiveSession partial before any DB write ─────────────────────
export function normalizeLiveSession<T extends Partial<LiveSession>>(data: T): T {
  const result = { ...data };
  if (result.status !== undefined) {
    result.status = normalizeSessionStatus(result.status) as any;
  }
  return result;
}

// ─── 8-second timeout wrapper for all Supabase RPC calls ─────────────────────
// Prevents any RPC from hanging the UI forever.
// Returns { success: false, error: 'Request timed out after 8 seconds' } on timeout.
export function withTimeout<T>(
  promise: Promise<T>,
  timeoutMs: number = 8000,
  label: string = 'RPC'
): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => {
      reject(new Error(`${label} timed out after ${timeoutMs / 1000} seconds`));
    }, timeoutMs);

    promise.then(
      (result) => { clearTimeout(timer); resolve(result); },
      (err) => { clearTimeout(timer); reject(err); }
    );
  });
}

// ─── In-memory request cache with TTL ────────────────────────────────────────
// Prevents repeated Supabase reads within a short window (5–10 seconds).
// If the same request is already in flight, returns the same promise.
// Call invalidateCache(key) after admin mutations to force a fresh fetch.

interface CacheEntry<T> {
  data: T;
  expiresAt: number;
}

const requestCache = new Map<string, CacheEntry<any>>();
const inFlightRequests = new Map<string, Promise<any>>();
const CACHE_TTL_MS = 3_000; // 3 seconds — reduced from 8s to prevent stale data on route transitions

export function invalidateCache(key?: string): void {
  if (key) {
    requestCache.delete(key);
    inFlightRequests.delete(key);
  } else {
    requestCache.clear();
    inFlightRequests.clear();
  }
}

function cachedRequest<T>(key: string, fn: () => Promise<T>, ttlMs = CACHE_TTL_MS): Promise<T> {
  // Return cached result if still fresh
  const cached = requestCache.get(key);
  if (cached && Date.now() < cached.expiresAt) {
    return Promise.resolve(cached.data as T);
  }

  // Return in-flight promise if already running
  const inFlight = inFlightRequests.get(key);
  if (inFlight) return inFlight as Promise<T>;

  // Start new request
  const promise = fn().then(
    (data) => {
      requestCache.set(key, { data, expiresAt: Date.now() + ttlMs });
      inFlightRequests.delete(key);
      return data;
    },
    (err) => {
      inFlightRequests.delete(key);
      throw err;
    }
  );

  inFlightRequests.set(key, promise);
  return promise;
}

export interface LiveSession {
  id: string;
  session_name_en: string;
  session_name_zh: string;
  day_of_week: string;
  time_slot: string;
  location: string;
  location_zh: string;
  meeting_point: string;
  meeting_point_zh: string;
  route_link: string;
  distance: string;
  duration: string;
  pace: string;
  hr_zone: string;
  intensity: string;
  goal_en: string;
  goal_zh: string;
  notes_en: string;
  notes_zh: string;
  suitable_for_en: string;
  suitable_for_zh: string;
  tags: string[];
  capacity: number;
  participants_count: number;
  status: string;
  host_name: string;
  week_id: string | null;
  week_status: string | null;
  template_id: string | null;
  created_at: string;
  updated_at: string;
}

// ─── Supabase session_templates row ──────────────────────────────────────────
// Maps directly to the session_templates table columns.
// id is always a real UUID from Supabase — never a tpl-* static string.
export interface SupabaseSessionTemplate {
  id: string;           // UUID — the only valid drag/apply payload
  name_en: string;
  name_zh: string;
  goal_en: string | null;
  goal_zh: string | null;
  notes_en: string | null;
  notes_zh: string | null;
  default_distance: string | null;
  default_duration: string | null;
  default_hr_zone: string | null;
  default_intensity: string | null;
  default_tags: string[];
  // Extended fields — may be null if not set in DB
  suggested_pace: string | null;
  suitable_for_en: string | null;
  suitable_for_zh: string | null;
  location_en: string | null;
  location_zh: string | null;
  meeting_point_en: string | null;
  meeting_point_zh: string | null;
  created_at: string;
}

// ─── Fetch all session templates from Supabase ────────────────────────────────
// Uses the same in-memory cache so repeated calls within 30s are free.
// Returns [] on error — callers should fall back to local static templates if needed.
export async function fetchSessionTemplates(): Promise<SupabaseSessionTemplate[]> {
  return cachedRequest('fetchSessionTemplates', async () => {
    const supabase = createClient();
    try {
      // First try full column set (after migration)
      const { data, error } = await withTimeout(
        supabase
          .from('session_templates')
          .select('id, name_en, name_zh, goal_en, goal_zh, notes_en, notes_zh, default_distance, default_duration, default_hr_zone, default_intensity, default_tags, suggested_pace, suitable_for_en, suitable_for_zh, location_en, location_zh, meeting_point_en, meeting_point_zh, created_at')
          .order('name_en', { ascending: true }),
        10000,
        'fetchSessionTemplates'
      );
      if (error) {
        // If error mentions a missing column, fall back to base columns only
        if (error.message && (error.message.includes('does not exist') || error.message.includes('column'))) {
          console.warn('[fetchSessionTemplates] extended columns missing, falling back to base columns:', error.message);
          const { data: baseData, error: baseError } = await withTimeout(
            supabase
              .from('session_templates')
              .select('id, name_en, name_zh, default_distance, default_duration, default_hr_zone, default_intensity, default_tags, created_at')
              .order('name_en', { ascending: true }),
            10000,
            'fetchSessionTemplates-fallback'
          );
          if (baseError) {
            console.error('[fetchSessionTemplates] fallback error:', baseError.message);
            return [];
          }
          // Map base data to full interface with nulls for missing fields
          return (baseData ?? []).map((row: any) => ({
            ...row,
            goal_en: null, goal_zh: null,
            notes_en: null, notes_zh: null,
            suggested_pace: null,
            suitable_for_en: null, suitable_for_zh: null,
            location_en: null, location_zh: null,
            meeting_point_en: null, meeting_point_zh: null,
          })) as SupabaseSessionTemplate[];
        }
        console.error('[fetchSessionTemplates] error:', error.message);
        return [];
      }
      return (data ?? []) as SupabaseSessionTemplate[];
    } catch (err: any) {
      console.error('[fetchSessionTemplates] exception:', err?.message);
      return [];
    }
  }, 30_000); // 30-second TTL — templates rarely change
}

export interface LiveParticipant {
  id: string;
  session_id: string;
  user_id: string;
  attendance_status: string;
  waitlist_position: number | null;
  joined_at: string;
  user_profiles?: {
    display_name: string;
    nickname: string;
    instagram: string;
    wechat: string;
    level: string;
    avatar: string;
  };
}

export interface AttendanceLog {
  id: string;
  session_id: string;
  user_id: string;
  status: 'present' | 'late' | 'absent';
  recorded_by: string;
  recorded_at: string;
  user_profiles?: {
    display_name: string;
    nickname: string;
  };
}

export interface WeekStatus {
  status: 'draft' | 'published' | 'confirmed';
  plan_id: string | null;
  confirmed_at: string | null;
  confirmed_by: string | null;
  published_at: string | null;
}

export interface DashboardMetrics {
  total_sessions: number;
  confirmed_sessions: number;
  total_participants: number;
  week_status: string;
}

// ─── Get or create current week plan, return its UUID ────────────────────────
export async function getOrCreateCurrentWeekPlan(): Promise<string | null> {
  const supabase = createClient();
  try {
    // Calculate current Monday
    const now = new Date();
    const day = now.getDay(); // 0=Sun, 1=Mon...
    const diff = day === 0 ? -6 : 1 - day; // days to Monday
    const monday = new Date(now);
    monday.setDate(now.getDate() + diff);
    const weekStart = monday.toISOString().split('T')[0];

    // Try to find existing plan
    const { data: existing } = await supabase
      .from('weekly_plans')
      .select('id')
      .eq('week_start_date', weekStart)
      .maybeSingle();

    if (existing?.id) {
      if (process.env.NODE_ENV === 'development') console.log('[sessions] currentWeekPlan loaded:', existing.id);
      return existing.id;
    }

    // Create new plan
    const { data: created, error } = await supabase
      .from('weekly_plans')
      .insert({ week_start_date: weekStart, status: 'draft' })
      .select('id')
      .single();

    if (error || !created?.id) {
      console.error('getOrCreateCurrentWeekPlan insert error:', error?.message);
      return null;
    }
    if (process.env.NODE_ENV === 'development') console.log('[sessions] currentWeekPlan created:', created.id);
    return created.id;
  } catch (err: any) {
    console.error('getOrCreateCurrentWeekPlan error:', err?.message);
    return null;
  }
}

// ─── Fetch sessions for current week only (by week_id) ───────────────────────
// Uses direct table queries instead of the unstable get_current_week_sessions RPC.
// Calculates current week's Monday date client-side, finds the matching weekly_plan,
// then fetches weekly_sessions for that plan. Falls back to all sessions if no plan found.
async function fetchWeeklySessionsDirect(supabase: ReturnType<typeof createClient>): Promise<LiveSession[]> {
  // Calculate current Monday using LOCAL date (not UTC) to avoid timezone-offset issues.
  // e.g. in UTC+8, toISOString() on a Monday morning returns Sunday's UTC date.
  const now = new Date();
  const localDay = now.getDay(); // 0=Sun, 1=Mon...
  const diffToMonday = localDay === 0 ? -6 : 1 - localDay;

  // Build a local YYYY-MM-DD string without UTC conversion
  const toLocalDateStr = (d: Date): string => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  };

  const localMonday = new Date(now);
  localMonday.setDate(now.getDate() + diffToMonday);

  // Try multiple candidate dates to handle any remaining timezone edge cases:
  // local Monday, day before, day after
  const candidates = [
    toLocalDateStr(localMonday),
    toLocalDateStr(new Date(localMonday.getTime() - 86400000)),
    toLocalDateStr(new Date(localMonday.getTime() + 86400000)),
  ];

  // Find the weekly_plan matching any candidate date
  const { data: plans, error: planError } = await withTimeout(
    supabase
      .from('weekly_plans')
      .select('id, status, week_start_date')
      .in('week_start_date', candidates),
    6000,
    'weekly_plans-lookup'
  );

  if (planError) {
    throw new Error(`weekly_plans lookup failed: ${planError.message}`);
  }

  let plan = plans && plans.length > 0 ? plans[0] : null;

  if (!plan?.id) {
    // No plan found for this week by any candidate date.
    // Fall back to fetching ALL non-cancelled sessions (same behaviour as get_dashboard_metrics).
    const { data: allSessions, error: allErr } = await withTimeout(
      supabase
        .from('weekly_sessions')
        .select('*')
        .neq('status', 'cancelled')
        .order('created_at', { ascending: true }),
      6000,
      'weekly_sessions-all-fallback'
    );
    if (allErr) {
      throw new Error(`weekly_sessions all-fallback failed: ${allErr.message}`);
    }
    return (allSessions || []) as LiveSession[];
  }

  // Fetch sessions for this week's plan
  const { data: sessions, error: sessionsError } = await withTimeout(
    supabase
      .from('weekly_sessions')
      .select('*')
      .eq('week_id', plan.id)
      .neq('status', 'cancelled')
      .order('created_at', { ascending: true }),
    6000,
    'weekly_sessions-fetch'
  );

  if (sessionsError) {
    throw new Error(`weekly_sessions fetch failed: ${sessionsError.message}`);
  }

  return (sessions || []) as LiveSession[];
}

export async function fetchWeeklySessions(): Promise<LiveSession[]> {
  return cachedRequest('fetchWeeklySessions', async () => {
    const supabase = createClient();

    // Primary: direct table query (no RPC dependency)
    try {
      const sessions = await fetchWeeklySessionsDirect(supabase);
      return sessions;
    } catch (primaryErr: any) {
      // Fallback: fetch all sessions without week filter
      try {
        const { data: fallback, error: fallbackErr } = await withTimeout(
          supabase
            .from('weekly_sessions')
            .select('*')
            .order('created_at', { ascending: true }),
          6000,
          'weekly_sessions-fallback'
        );
        if (fallbackErr) {
          return [];
        }
        return (fallback || []) as LiveSession[];
      } catch {
        return [];
      }
    }
  });
}

// ─── Repair sessions with NULL week_id (client-side helper) ──────────────────
export async function repairNullWeekIdSessions(): Promise<{ repaired: number; weekId: string | null }> {
  const supabase = createClient();
  try {
    // Check for NULL week_id sessions
    const { data: nullSessions } = await supabase
      .from('weekly_sessions')
      .select('id')
      .is('week_id', null);

    if (!nullSessions || nullSessions.length === 0) {
      return { repaired: 0, weekId: null };
    }

    // Get or create current week plan
    const weekId = await getOrCreateCurrentWeekPlan();
    if (!weekId) return { repaired: 0, weekId: null };

    // Update all NULL week_id sessions
    const { error } = await supabase
      .from('weekly_sessions')
      .update({ week_id: weekId, updated_at: new Date().toISOString() })
      .is('week_id', null);

    if (error) {
      console.error('repairNullWeekIdSessions error:', error.message);
      return { repaired: 0, weekId };
    }

    if (process.env.NODE_ENV === 'development') {
      console.log('[sessions] sessions repaired count:', nullSessions.length, '→ week_id:', weekId);
    }
    return { repaired: nullSessions.length, weekId };
  } catch (err: any) {
    console.error('repairNullWeekIdSessions unexpected error:', err?.message);
    return { repaired: 0, weekId: null };
  }
}

// ─── Fetch participants for a session ────────────────────────────────────────
export async function fetchSessionParticipants(sessionId: string): Promise<LiveParticipant[]> {
  const supabase = createClient();
  const { data, error } = await supabase
    .from('session_participants')
    .select(`
      *,
      user_profiles (
        display_name,
        nickname,
        instagram,
        wechat,
        level,
        avatar
      )
    `)
    .eq('session_id', sessionId)
    .neq('attendance_status', 'cancelled')
    .order('joined_at', { ascending: true });

  if (error) {
    console.error('fetchSessionParticipants error:', error.message);
    return [];
  }
  return (data || []) as LiveParticipant[];
}

// ─── Fetch attendance logs for a session ─────────────────────────────────────
export async function fetchAttendanceLogs(sessionId: string): Promise<AttendanceLog[]> {
  const supabase = createClient();

  // Step 1: Fetch attendance_logs without embedding user_profiles
  // (avoids ambiguous FK relationship error between attendance_logs and user_profiles)
  const { data: logs, error } = await supabase
    .from('attendance_logs')
    .select('id, session_id, user_id, status, recorded_by, recorded_at')
    .eq('session_id', sessionId)
    .order('recorded_at', { ascending: true });

  if (error) {
    console.error('fetchAttendanceLogs error:', error.message);
    return [];
  }

  const safeLog = logs ?? [];
  if (process.env.NODE_ENV === 'development') {
    console.log('[Participants] attendance logs loaded:', safeLog.length);
  }

  if (safeLog.length === 0) {
    return [];
  }

  // Step 2: Collect unique user_ids (filter out nulls)
  const userIds = [...new Set(safeLog.map((l) => l.user_id).filter(Boolean))] as string[];

  // Step 3: Fetch user_profiles separately
  let profileMap: Record<string, { display_name: string; nickname: string }> = {};

  if (userIds.length > 0) {
    const { data: profiles, error: profileError } = await supabase
      .from('user_profiles')
      .select('id, display_name, nickname')
      .in('id', userIds);

    if (profileError) {
      console.error('[Participants] profiles fetch error:', profileError.message);
      // Continue without profiles — fallback values will be used
    } else {
      const safeProfiles = profiles ?? [];
      if (process.env.NODE_ENV === 'development') {
        console.log('[Participants] profiles loaded:', safeProfiles.length);
      }
      // Build a map keyed by profile id for O(1) lookup
      for (const p of safeProfiles) {
        if (p?.id) {
          profileMap[p.id] = {
            display_name: p.display_name ?? 'Unknown Member',
            nickname: p.nickname ?? '',
          };
        }
      }
    }
  }

  // Step 4: Merge profile data into attendance logs client-side
  const merged: AttendanceLog[] = safeLog.map((log) => ({
    id: log.id,
    session_id: log.session_id,
    user_id: log.user_id ?? '',
    status: log.status as 'present' | 'late' | 'absent',
    recorded_by: log.recorded_by ?? '',
    recorded_at: log.recorded_at ?? '',
    user_profiles: log.user_id && profileMap[log.user_id]
      ? profileMap[log.user_id]
      : { display_name: 'Unknown Member', nickname: '' },
  }));

  if (process.env.NODE_ENV === 'development') {
    console.log('[Participants] merged rows:', merged.length);
  }

  return merged;
}

// ─── Join session ─────────────────────────────────────────────────────────────
export async function joinSession(sessionId: string): Promise<{ success: boolean; status?: string; position?: number; error?: string }> {
  const supabase = createClient();
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return { success: false, error: 'not_authenticated' };

  const { data, error } = await supabase.rpc('join_session', {
    p_session_id: sessionId,
    p_user_id: user.id,
  });

  if (error) {
    console.error('joinSession error:', error.message);
    return { success: false, error: error.message };
  }
  // Invalidate sessions cache so participant counts refresh
  invalidateCache('fetchWeeklySessions');
  return data as { success: boolean; status?: string; position?: number; error?: string };
}

// ─── Leave session ────────────────────────────────────────────────────────────
export async function leaveSession(sessionId: string): Promise<{ success: boolean; error?: string }> {
  const supabase = createClient();
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return { success: false, error: 'not_authenticated' };

  const { data, error } = await supabase.rpc('leave_session', {
    p_session_id: sessionId,
    p_user_id: user.id,
  });

  if (error) {
    console.error('leaveSession error:', error.message);
    return { success: false, error: error.message };
  }
  // Invalidate sessions cache so participant counts refresh
  invalidateCache('fetchWeeklySessions');
  return data as { success: boolean; error?: string };
}

// ─── Get user's participation status for a session ───────────────────────────
export async function getUserSessionStatus(sessionId: string): Promise<string | null> {
  const supabase = createClient();
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return null;

  const { data, error } = await supabase
    .from('session_participants')
    .select('attendance_status')
    .eq('session_id', sessionId)
    .eq('user_id', user.id)
    .neq('attendance_status', 'cancelled')
    .maybeSingle();

  if (error || !data) return null;
  return data.attendance_status;
}

// ─── Get all user's session statuses ─────────────────────────────────────────
export async function getUserAllSessionStatuses(): Promise<Record<string, string>> {
  const supabase = createClient();
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return {};

  const { data, error } = await supabase
    .from('session_participants')
    .select('session_id, attendance_status')
    .eq('user_id', user.id)
    .neq('attendance_status', 'cancelled');

  if (error || !data) return {};
  return Object.fromEntries(data.map((r: any) => [r.session_id, r.attendance_status]));
}

// ─── Record attendance (admin/coach) ─────────────────────────────────────────
export async function recordAttendance(
  sessionId: string,
  userId: string,
  status: 'present' | 'late' | 'absent'
): Promise<{ success: boolean; error?: string }> {
  const supabase = createClient();
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return { success: false, error: 'not_authenticated' };

  const { data, error } = await supabase.rpc('record_attendance', {
    p_session_id: sessionId,
    p_user_id: userId,
    p_status: status,
    p_recorded_by: user.id,
  });

  if (error) {
    console.error('recordAttendance error:', error.message);
    return { success: false, error: error.message };
  }
  return data as { success: boolean; error?: string };
}

// ─── Update a single session field directly in Supabase ──────────────────────
export async function updateSessionField(
  sessionId: string,
  updates: Partial<LiveSession>
): Promise<{ success: boolean; error?: string }> {
  const supabase = createClient();
  // Normalize status enum before writing
  const normalized = normalizeLiveSession(updates);
  const { error } = await supabase
    .from('weekly_sessions')
    .update({ ...normalized, updated_at: new Date().toISOString() })
    .eq('id', sessionId);

  if (error) {
    console.error('updateSessionField error:', error.message);
    return { success: false, error: error.message };
  }
  if (process.env.NODE_ENV === 'development') console.log('[sessions] Updated session ID:', sessionId);
  // Invalidate cache after mutation
  invalidateCache();
  return { success: true };
}

// ─── Create a new session row in Supabase and return the real UUID ────────────
export async function createSession(
  sessionData: Omit<Partial<LiveSession>, 'id'>
): Promise<{ success: boolean; id?: string; error?: string }> {
  const supabase = createClient();
  const normalized = normalizeLiveSession(sessionData);

  // For new sessions (especially from templates), always default to 'planned'.
  // normalizeSessionStatus ensures no UI label (Draft/Planned/etc.) reaches the DB.
  if (!normalized.status) {
    normalized.status = 'planned';
  } else {
    normalized.status = normalizeSessionStatus(normalized.status, 'planned');
  }

  // Auto-assign week_id if not provided
  if (!normalized.week_id) {
    const weekId = await getOrCreateCurrentWeekPlan();
    if (weekId) normalized.week_id = weekId;
  }

  // Use SECURITY DEFINER RPC to bypass RLS for admin/coach inserts
  const { data, error } = await supabase.rpc('create_weekly_session', {
    p_day_of_week:      normalized.day_of_week      ?? 'Monday',
    p_session_name_en:  normalized.session_name_en  ?? '',
    p_session_name_zh:  normalized.session_name_zh  ?? '',
    p_time_slot:        normalized.time_slot         ?? '',
    p_location:         normalized.location          ?? '',
    p_location_zh:      normalized.location_zh       ?? '',
    p_meeting_point:    normalized.meeting_point     ?? '',
    p_meeting_point_zh: normalized.meeting_point_zh  ?? '',
    p_route_link:       normalized.route_link        ?? '',
    p_distance:         normalized.distance          ?? '',
    p_duration:         normalized.duration          ?? '',
    p_pace:             normalized.pace              ?? '',
    p_hr_zone:          normalized.hr_zone           ?? '',
    p_intensity:        normalized.intensity         ?? 'Low',
    p_goal_en:          normalized.goal_en           ?? '',
    p_goal_zh:          normalized.goal_zh           ?? '',
    p_notes_en:         normalized.notes_en          ?? '',
    p_notes_zh:         normalized.notes_zh          ?? '',
    p_suitable_for_en:  normalized.suitable_for_en   ?? '',
    p_suitable_for_zh:  normalized.suitable_for_zh   ?? '',
    p_tags:             normalized.tags              ?? [],
    p_capacity:         normalized.capacity          ?? 25,
    p_host_name:        normalized.host_name         ?? '',
    // Always send lowercase enum value — never UI labels like 'Draft'/'Planned'
    p_status:           normalizeSessionStatus(normalized.status ?? 'planned', 'planned'),
    p_template_id:      normalized.template_id       ?? null,
    p_week_id:          normalized.week_id           ?? null,
  });

  if (error) {
    console.error('createSession error:', error.message);
    return { success: false, error: error.message };
  }

  const result = data as { success: boolean; id?: string; error?: string };
  if (!result?.success) {
    console.error('createSession RPC error:', result?.error);
    return { success: false, error: result?.error ?? 'unknown_error' };
  }

  if (process.env.NODE_ENV === 'development') console.log('[sessions] Created session ID:', result.id);
  // Invalidate cache after mutation
  invalidateCache();
  return { success: true, id: result.id };
}

// ─── Save session (admin) ─────────────────────────────────────────────────────
export async function saveSession(sessionData: Partial<LiveSession> & { id?: string }): Promise<{ success: boolean; id?: string; error?: string }> {
  const supabase = createClient();
  const normalized = normalizeLiveSession(sessionData);

  if (normalized.id) {
    const { id, ...updates } = normalized;
    const { error } = await supabase
      .from('weekly_sessions')
      .update({ ...updates, updated_at: new Date().toISOString() })
      .eq('id', id);

    if (error) return { success: false, error: error.message };
    invalidateCache();
    return { success: true, id };
  } else {
    const { data, error } = await supabase
      .from('weekly_sessions')
      .insert(normalized)
      .select('id')
      .single();

    if (error) return { success: false, error: error.message };
    invalidateCache();
    return { success: true, id: data?.id };
  }
}

// ─── Delete session (admin) ───────────────────────────────────────────────────
export async function deleteSession(sessionId: string): Promise<{ success: boolean; error?: string }> {
  const supabase = createClient();
  const { error } = await supabase
    .from('weekly_sessions')
    .delete()
    .eq('id', sessionId);

  if (error) return { success: false, error: error.message };
  invalidateCache();
  return { success: true };
}

// ─── Save Draft: persist all sessions to DB as draft ─────────────────────────
// Wrapped with 8-second timeout — always resolves, never hangs
export async function saveDraftToDB(sessions: Partial<LiveSession>[]): Promise<{ success: boolean; error?: string }> {
  const supabase = createClient();
  // Normalize all statuses before sending
  const normalizedSessions = sessions.map(normalizeLiveSession);
  try {
    const { data, error } = await withTimeout(
      supabase.rpc('save_week_draft', { p_sessions: normalizedSessions }),
      8000,
      'save_week_draft'
    );
    if (error) {
      console.error('saveDraftToDB error:', error.message);
      return { success: false, error: error.message };
    }
    invalidateCache();
    return (data as any) || { success: true };
  } catch (err: any) {
    console.error('saveDraftToDB timeout/error:', err?.message);
    return { success: false, error: err?.message || 'Save draft timed out' };
  }
}

// ─── Publish Week ─────────────────────────────────────────────────────────────
// Wrapped with 8-second timeout — always resolves, never hangs
export async function publishWeek(): Promise<{ success: boolean; status?: string; week_id?: string; error?: string }> {
  const supabase = createClient();
  if (process.env.NODE_ENV === 'development') {
    console.log('[publishWeekInDB] calling publish_week RPC');
  }
  try {
    const { data, error } = await withTimeout(
      supabase.rpc('publish_week'),
      8000,
      'publish_week'
    );
    if (error) {
      console.error('publishWeekInDB error:', error.message, '| code:', error.code);
      return { success: false, error: error.message };
    }
    invalidateCache();
    const result = (data as any) || { success: true };
    if (process.env.NODE_ENV === 'development') {
      console.log('[publishWeekInDB] result:', result);
    }
    return result;
  } catch (err: any) {
    console.error('publishWeekInDB timeout/error:', err?.message);
    return { success: false, error: err?.message || 'Publish timed out after 8 seconds' };
  }
}

// ─── Confirm Week ─────────────────────────────────────────────────────────────
// Wrapped with 8-second timeout — always resolves, never hangs
export async function confirmWeekInDB(): Promise<{ success: boolean; status?: string; week_id?: string; confirmed_sessions_count?: number; error?: string }> {
  const supabase = createClient();
  if (process.env.NODE_ENV === 'development') {
    console.log('[confirmWeekInDB] calling confirm_week RPC');
  }
  try {
    const { data, error } = await withTimeout(
      supabase.rpc('confirm_week'),
      8000,
      'confirm_week'
    );
    if (error) {
      console.error('confirmWeekInDB error:', error.message, '| code:', error.code);
      return { success: false, error: error.message };
    }
    invalidateCache();
    const result = (data as any) || { success: true };
    if (process.env.NODE_ENV === 'development') {
      console.log('[confirmWeekInDB] result:', result);
    }
    return result;
  } catch (err: any) {
    console.error('confirmWeekInDB timeout/error:', err?.message);
    return { success: false, error: err?.message || 'Confirm week timed out after 8 seconds' };
  }
}

// ─── Get current week status ──────────────────────────────────────────────────
// Wrapped with 8-second timeout — always resolves with safe fallback
export async function getCurrentWeekStatus(): Promise<WeekStatus> {
  return cachedRequest('getCurrentWeekStatus', async () => {
    const supabase = createClient();
    try {
      const { data, error } = await withTimeout(
        supabase.rpc('get_current_week_status'),
        8000,
        'get_current_week_status'
      );
      if (error || !data) {
        // Fallback: query weekly_plans directly for the most recent plan
        const { data: plan } = await supabase
          .from('weekly_plans')
          .select('id, status, confirmed_at, confirmed_by, published_at')
          .order('week_start_date', { ascending: false })
          .limit(1)
          .maybeSingle();
        if (plan) {
          return {
            status: plan.status || 'draft',
            plan_id: plan.id,
            confirmed_at: plan.confirmed_at,
            confirmed_by: plan.confirmed_by,
            published_at: plan.published_at,
          } as WeekStatus;
        }
        return { status: 'draft', plan_id: null, confirmed_at: null, confirmed_by: null, published_at: null };
      }
      // If RPC returned but plan_id is null, try direct fallback
      const result = data as WeekStatus;
      if (!result.plan_id) {
        const { data: plan } = await supabase
          .from('weekly_plans')
          .select('id, status, confirmed_at, confirmed_by, published_at')
          .order('week_start_date', { ascending: false })
          .limit(1)
          .maybeSingle();
        if (plan) {
          return {
            status: plan.status || 'draft',
            plan_id: plan.id,
            confirmed_at: plan.confirmed_at,
            confirmed_by: plan.confirmed_by,
            published_at: plan.published_at,
          } as WeekStatus;
        }
      }
      return result;
    } catch (err: any) {
      console.error('getCurrentWeekStatus timeout/error:', err?.message);
      return { status: 'draft', plan_id: null, confirmed_at: null, confirmed_by: null, published_at: null };
    }
  });
}

// ─── Get live dashboard metrics ───────────────────────────────────────────────
// Wrapped with 8-second timeout — always resolves with safe zero defaults.
// Falls back to direct table queries when RPC returns zeros (timezone mismatch safety net).
export async function getDashboardMetrics(): Promise<DashboardMetrics> {
  return cachedRequest('getDashboardMetrics', async () => {
    const supabase = createClient();
    try {
      const { data, error } = await withTimeout(
        supabase.rpc('get_dashboard_metrics'),
        8000,
        'get_dashboard_metrics'
      );
      if (!error && data) {
        const metrics = data as DashboardMetrics;
        // If RPC returned non-zero data, trust it
        if (metrics.total_sessions > 0) {
          return metrics;
        }
      }

      // Fallback: compute metrics directly from weekly_sessions + weekly_plans
      // Always use the most recent plan (not just current week) to avoid timezone issues.
      try {
        // Try current week candidates first (±1 day for timezone safety)
        const now = new Date();
        const localDay = now.getDay();
        const diffToMonday = localDay === 0 ? -6 : 1 - localDay;
        const localMonday = new Date(now);
        localMonday.setDate(now.getDate() + diffToMonday);
        const toLocalDateStr = (d: Date): string => {
          const y = d.getFullYear();
          const m = String(d.getMonth() + 1).padStart(2, '0');
          const day = String(d.getDate()).padStart(2, '0');
          return `${y}-${m}-${day}`;
        };
        const candidates = [
          toLocalDateStr(localMonday),
          toLocalDateStr(new Date(localMonday.getTime() - 86400000)),
          toLocalDateStr(new Date(localMonday.getTime() + 86400000)),
        ];

        // Try to find plan for current week first
        const { data: weekPlans } = await supabase
          .from('weekly_plans')
          .select('id, status, week_start_date')
          .in('week_start_date', candidates)
          .order('week_start_date', { ascending: false })
          .limit(1);

        // Fall back to most recent plan if no current week plan found
        let plan: { id: string; status: string; week_start_date: string } | null =
          weekPlans && weekPlans.length > 0 ? weekPlans[0] : null;

        if (!plan) {
          const { data: recentPlans } = await supabase
            .from('weekly_plans')
            .select('id, status, week_start_date')
            .order('week_start_date', { ascending: false })
            .limit(1);
          plan = recentPlans && recentPlans.length > 0 ? recentPlans[0] : null;
        }

        if (!plan) {
          // No plan at all — count all non-cancelled sessions
          const { data: allSessions } = await supabase
            .from('weekly_sessions')
            .select('status, participants_count')
            .neq('status', 'cancelled');

          const sessions = allSessions || [];
          return {
            total_sessions: sessions.length,
            confirmed_sessions: sessions.filter((s: any) => (s.status || '').toLowerCase() === 'confirmed').length,
            total_participants: sessions.reduce((sum: number, s: any) => sum + (s.participants_count || 0), 0),
            week_status: 'draft',
          };
        }

        const { data: weekSessions } = await supabase
          .from('weekly_sessions')
          .select('status, participants_count')
          .eq('week_id', plan.id)
          .neq('status', 'cancelled');

        const sessions = weekSessions || [];
        return {
          total_sessions: sessions.length,
          confirmed_sessions: sessions.filter((s: any) => (s.status || '').toLowerCase() === 'confirmed').length,
          total_participants: sessions.reduce((sum: number, s: any) => sum + (s.participants_count || 0), 0),
          week_status: plan.status || 'draft',
        };
      } catch (fallbackErr: any) {
        console.error('getDashboardMetrics fallback error:', fallbackErr?.message);
        return { total_sessions: 0, confirmed_sessions: 0, total_participants: 0, week_status: 'draft' };
      }
    } catch (err: any) {
      console.error('getDashboardMetrics timeout/error:', err?.message);
      return { total_sessions: 0, confirmed_sessions: 0, total_participants: 0, week_status: 'draft' };
    }
  });
}

// ─── Seed default week: insert 7 default sessions with real UUIDs ─────────────
export async function seedDefaultWeek(
  templates: Array<{
    day_of_week: string;
    session_name_en: string;
    session_name_zh: string;
    time_slot: string;
    location: string;
    location_zh: string;
    distance?: string;
    duration?: string;
    pace?: string;
    hr_zone?: string;
    intensity: string;
    goal_en: string;
    goal_zh: string;
    notes_en: string;
    notes_zh: string;
    suitable_for_en: string;
    suitable_for_zh: string;
    capacity: number;
    tags: string[];
    host_name?: string;
    meeting_point?: string;
    meeting_point_zh?: string;
    route_link?: string;
  }>
): Promise<{ success: boolean; ids?: string[]; error?: string }> {
  const supabase = createClient();

  // Use SECURITY DEFINER RPC to bypass RLS for admin/coach bulk inserts
  // The RPC now auto-assigns week_id to current week plan
  const rows = templates.map((t) => ({
    ...t,
    status: 'planned',
  }));

  const { data, error } = await supabase.rpc('seed_weekly_sessions', {
    p_sessions: rows,
  });

  if (error) {
    console.error('seedDefaultWeek error:', error.message);
    return { success: false, error: error.message };
  }

  const result = data as { success: boolean; ids?: string[]; error?: string };
  if (!result?.success) {
    console.error('seedDefaultWeek RPC error:', result?.error);
    return { success: false, error: result?.error ?? 'unknown_error' };
  }

  const ids = result.ids ?? [];
  if (process.env.NODE_ENV === 'development') console.log('[sessions] Seeded', ids.length, 'sessions with real UUIDs:', ids);
  invalidateCache();
  return { success: true, ids };
}

// ─── Fetch member's session history ──────────────────────────────────────────
export async function fetchMemberSessionHistory(userId: string): Promise<any[]> {
  const supabase = createClient();
  const { data, error } = await supabase
    .from('session_participants')
    .select(`
      *,
      weekly_sessions (
        session_name_en,
        session_name_zh,
        day_of_week,
        time_slot,
        location,
        status
      )
    `)
    .eq('user_id', userId)
    .order('joined_at', { ascending: false });

  if (error) return [];
  return data || [];
}

// ─── Fetch member's attendance history ───────────────────────────────────────
export async function fetchMemberAttendanceHistory(userId: string): Promise<any[]> {
  const supabase = createClient();
  const { data, error } = await supabase
    .from('attendance_logs')
    .select(`
      *,
      weekly_sessions (
        session_name_en,
        session_name_zh,
        day_of_week,
        time_slot
      )
    `)
    .eq('user_id', userId)
    .order('recorded_at', { ascending: false });

  if (error) return [];
  return data || [];
}

// ─── Replace an existing session with a template (admin) ─────────────────────
// FIXED: accepts templateId as a plain UUID string — never a template object.
// Validates both IDs as UUIDs before any RPC call.
// On failure: does NOT mutate local state, does NOT invalidate cache.
export async function replaceSessionWithTemplate(
  existingSessionId: string,
  templateId: string,
  selectedDay: string,
  templateFields: {
    name: string;
    nameZH: string;
    time?: string;
    defaultLocation?: string;
    defaultLocationZH?: string;
    distance?: string;
    duration?: string;
    pace?: string;
    hrZone?: string;
    intensity: string;
    goal: string;
    goalZH: string;
    notes: string;
    notesZH: string;
    suitableFor: string;
    suitableForZH: string;
    tags: string[];
    capacity?: number;
  },
  currentSession?: Partial<LiveSession>
): Promise<{ success: boolean; error?: string }> {
  const supabase = createClient();

  // ─── UUID validation guard ────────────────────────────────────────────────
  const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

  // Log what we received for debugging
  if (process.env.NODE_ENV === 'development') {
    console.log('[DND] templateId received:', templateId, typeof templateId);
    console.log('[DND] selectedDay:', selectedDay);
    console.log('[DND] existingSessionId:', existingSessionId);
  }

  // Reject if templateId is not a plain UUID string
  if (!templateId || typeof templateId !== 'string' || !UUID_REGEX.test(templateId)) {
    const errMsg = `Template ID type mismatch. Expected UUID string, got: ${JSON.stringify(templateId)}`;
    console.error('[replaceSessionWithTemplate] INVALID templateId (not a UUID):', {
      templateId,
      existingSessionId,
      selectedDay,
      templateName: templateFields.name,
    });
    return { success: false, error: errMsg };
  }

  if (!existingSessionId || typeof existingSessionId !== 'string' || !UUID_REGEX.test(existingSessionId)) {
    const errMsg = `Session ID type mismatch. Expected UUID string, got: ${JSON.stringify(existingSessionId)}`;
    console.error('[replaceSessionWithTemplate] INVALID existingSessionId (not a UUID):', {
      existingSessionId,
      templateId,
      selectedDay,
      templateName: templateFields.name,
    });
    return { success: false, error: errMsg };
  }

  // Log the action
  if (process.env.NODE_ENV === 'development') {
    console.log('[DND] action: replace', {
      existingSessionId,
      templateId,
      selectedDay,
      templateName: templateFields.name,
    });
  }

  try {
    const { data, error } = await withTimeout(
      supabase.rpc('replace_session_with_template', {
        p_session_id:       existingSessionId,
        p_template_id:      templateId,
        p_session_name_en:  templateFields.name,
        p_session_name_zh:  templateFields.nameZH,
        p_time_slot:        templateFields.time || currentSession?.time_slot || '07:30',
        p_location:         templateFields.defaultLocation || currentSession?.location || 'Victoria Park',
        p_location_zh:      templateFields.defaultLocationZH || currentSession?.location_zh || '维多利亚公园',
        p_meeting_point:    currentSession?.meeting_point || '',
        p_meeting_point_zh: currentSession?.meeting_point_zh || '',
        p_route_link:       currentSession?.route_link || '',
        p_distance:         templateFields.distance || '',
        p_duration:         templateFields.duration || '',
        p_pace:             templateFields.pace || '',
        p_hr_zone:          templateFields.hrZone || '',
        p_intensity:        templateFields.intensity,
        p_goal_en:          templateFields.goal,
        p_goal_zh:          templateFields.goalZH,
        p_notes_en:         templateFields.notes,
        p_notes_zh:         templateFields.notesZH,
        p_suitable_for_en:  templateFields.suitableFor,
        p_suitable_for_zh:  templateFields.suitableForZH,
        p_tags:             templateFields.tags,
        p_capacity:         templateFields.capacity ?? currentSession?.capacity ?? 25,
        p_host_name:        currentSession?.host_name || '',
        p_status:           normalizeSessionStatus(currentSession?.status || 'planned', 'planned'),
      }),
      15000,
      'replace_session_with_template'
    );

    if (error) {
      console.error('[replaceSessionWithTemplate] RPC error:', error.message, {
        existingSessionId,
        templateId,
        selectedDay,
        templateName: templateFields.name,
      });
      // Do NOT invalidate cache on failure — prevents loading corruption
      return { success: false, error: 'Could not apply template. Please try again.' };
    }

    const result = data as { success: boolean; error?: string };
    if (!result?.success) {
      console.error('[replaceSessionWithTemplate] RPC returned failure:', result?.error, {
        existingSessionId,
        templateId,
        selectedDay,
        templateName: templateFields.name,
      });
      // Do NOT invalidate cache on failure
      return { success: false, error: 'Could not apply template. Please try again.' };
    }

    if (process.env.NODE_ENV === 'development') {
      console.log('[replaceSessionWithTemplate] success — session:', existingSessionId, '→ template:', templateId, templateFields.name);
    }
    // Only invalidate cache after confirmed success
    invalidateCache();
    return { success: true };
  } catch (err: any) {
    console.error('[replaceSessionWithTemplate] timeout/unexpected error:', err?.message, {
      existingSessionId,
      templateId,
      selectedDay,
      templateName: templateFields.name,
    });
    // Do NOT invalidate cache on failure
    return { success: false, error: 'Could not apply template. Please try again.' };
  }
}

// ─── Create session from template (upsert) ───────────────────────────────────
// Uses create_session_from_template RPC with ON CONFLICT DO UPDATE.
// If a session already exists for (week_id, day_of_week), it UPDATES that row.
// Never causes duplicate key errors.
export async function createSessionFromTemplate(
  weekId: string,
  templateId: string,
  selectedDay: string,
  templateFields: {
    name: string;
    nameZH: string;
    distance?: string | null;
    duration?: string | null;
    hrZone?: string | null;
    intensity: string;
    goal: string;
    goalZH: string;
    notes: string;
    notesZH: string;
    tags: string[];
    // Accept both naming conventions for pace and suitable_for
    suggested_pace?: string | null;
    pace?: string | null;
    suitable_for_en?: string | null;
    suitable_for_zh?: string | null;
    suitableFor?: string | null;
    suitableForZH?: string | null;
  }
): Promise<{ success: boolean; id?: string; error?: string }> {
  const supabase = createClient();

  const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

  if (!weekId || !UUID_REGEX.test(weekId)) {
    return { success: false, error: 'Invalid week_id' };
  }
  if (!templateId || !UUID_REGEX.test(templateId)) {
    return { success: false, error: 'Invalid template_id' };
  }

  if (process.env.NODE_ENV === 'development') {
    console.log('[DND] action: create_session_from_template', { weekId, templateId, selectedDay, templateName: templateFields.name });
  }

  // Resolve pace and suitable_for from either naming convention
  const resolvedPace = templateFields.pace || templateFields.suggested_pace || '';
  const resolvedSuitableForEn = templateFields.suitableFor || templateFields.suitable_for_en || '';
  const resolvedSuitableForZh = templateFields.suitableForZH || templateFields.suitable_for_zh || '';

  try {
    const { data, error } = await withTimeout(
      supabase.rpc('create_session_from_template', {
        p_week_id:          weekId,
        p_template_id:      templateId,
        p_selected_day:     selectedDay,
        p_session_name_en:  templateFields.name,
        p_session_name_zh:  templateFields.nameZH,
        p_time_slot:        '07:30',
        p_location:         'Victoria Park',
        p_location_zh:      '维多利亚公园',
        p_meeting_point:    '',
        p_meeting_point_zh: '',
        p_route_link:       '',
        p_distance:         templateFields.distance || '',
        p_duration:         templateFields.duration || '',
        p_pace:             resolvedPace,
        p_hr_zone:          templateFields.hrZone || '',
        p_intensity:        templateFields.intensity || 'Low',
        p_goal_en:          templateFields.goal,
        p_goal_zh:          templateFields.goalZH,
        p_notes_en:         templateFields.notes,
        p_notes_zh:         templateFields.notesZH,
        p_suitable_for_en:  resolvedSuitableForEn,
        p_suitable_for_zh:  resolvedSuitableForZh,
        p_tags:             templateFields.tags || [],
        p_capacity:         25,
        p_host_name:        '',
        p_status:           'planned',
      }),
      15000,
      'create_session_from_template'
    );

    if (error) {
      console.error('[createSessionFromTemplate] RPC error:', error.message);
      return { success: false, error: error.message };
    }

    const result = data as { success: boolean; id?: string; error?: string };
    if (!result?.success) {
      console.error('[createSessionFromTemplate] RPC returned failure:', result?.error);
      return { success: false, error: result?.error ?? 'unknown_error' };
    }

    if (process.env.NODE_ENV === 'development') {
      console.log('[createSessionFromTemplate] success — id:', result.id, 'day:', selectedDay);
    }
    invalidateCache();
    return { success: true, id: result.id };
  } catch (err: any) {
    console.error('[createSessionFromTemplate] timeout/unexpected error:', err?.message);
    return { success: false, error: err?.message || 'Request timed out' };
  }
}

// ─── Swap two sessions (transaction-safe) ────────────────────────────────────
// Uses swap_sessions RPC which uses a temporary day value to avoid unique constraint violations.
// UI must only update after confirmed DB success.
export async function swapSessions(
  sessionAId: string,
  sessionBId: string
): Promise<{ success: boolean; sessionA?: Partial<LiveSession>; sessionB?: Partial<LiveSession>; error?: string }> {
  const supabase = createClient();

  const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

  if (!sessionAId || !UUID_REGEX.test(sessionAId)) {
    return { success: false, error: 'Invalid session_a_id' };
  }
  if (!sessionBId || !UUID_REGEX.test(sessionBId)) {
    return { success: false, error: 'Invalid session_b_id' };
  }

  if (process.env.NODE_ENV === 'development') {
    console.log('[swap_sessions] swapping:', sessionAId, '↔', sessionBId);
  }

  try {
    const { data, error } = await withTimeout(
      supabase.rpc('swap_sessions', {
        p_session_a_id: sessionAId,
        p_session_b_id: sessionBId,
      }),
      15000,
      'swap_sessions'
    );

    if (error) {
      console.error('[swapSessions] RPC error:', error.message);
      return { success: false, error: error.message };
    }

    const result = data as { success: boolean; session_a?: any; session_b?: any; error?: string; note?: string };
    if (!result?.success) {
      console.error('[swapSessions] RPC returned failure:', result?.error);
      return { success: false, error: result?.error ?? 'Swap failed' };
    }

    if (process.env.NODE_ENV === 'development') {
      console.log('[swapSessions] success:', result);
    }
    // Only invalidate cache after confirmed success
    invalidateCache();
    return {
      success: true,
      sessionA: result.session_a as Partial<LiveSession>,
      sessionB: result.session_b as Partial<LiveSession>,
    };
  } catch (err: any) {
    console.error('[swapSessions] timeout/unexpected error:', err?.message);
    return { success: false, error: err?.message || 'Swap timed out' };
  }
}

// ─── Get current week sessions (alias for public page) ───────────────────────
export async function getCurrentWeekSessions(): Promise<LiveSession[]> {
  return fetchWeeklySessions();
}